import React, { useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { useTranslation } from "react-i18next";
import BaseDatepicker from "@reusables/BaseDatepicker";
import { SaleOrdersFormTyping } from "../../types";
import { useGetModuleCustomersQuery } from "@redux/features/customers/customersApi";
import CreateCustomerModal from "./CreateCustomerModal";
import { useLocation } from "react-router-dom";
import { BadgeStatus } from "@/types/general";
import { getOrderStatusIcon } from "@reusables/BaseOrderStatusBadge";

interface RouterStateType {
    customer: {
        id: number;
        name: string;
        code?: string;
    };
}

export default function GeneralSection() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general" });
    const { t: statusesT } = useTranslation("", { keyPrefix: "general.orders.statuses.base" });

    const location = useLocation<RouterStateType>();

    const routerState = location.state;

    const { control, setValue } = useFormContext<SaleOrdersFormTyping>();

    // Controlling "create customer" modal in-place to keep customer selection flow continuous.
    const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = React.useState(false);

    const isInvoiced = useWatch({
        control,
        name: "is_invoiced",
    });

    const mode = useWatch({
        control,
        name: "mode",
    });

    const status = useWatch({
        control,
        name: "status",
    });

    const initialEditingStatusRef = useRef<BadgeStatus.Base | undefined>();

    useEffect(() => {
        // Capturing initial editing status once, enabling Offer-specific edit flow checks later.
        if (mode === "creation") {
            initialEditingStatusRef.current = undefined;
            return;
        }

        if (mode === "editing" && status && !initialEditingStatusRef.current) {
            initialEditingStatusRef.current = status;
        }
    }, [mode, status]);

    const statusOptions = useMemo(
        () => [
            {
                label: statusesT(BadgeStatus.Base.offer),
                value: BadgeStatus.Base.offer,
            },
            {
                label: statusesT(BadgeStatus.Base.new),
                value: BadgeStatus.Base.new,
            },
        ],
        [statusesT]
    );

    const isEditableOfferFlow =
        mode === "editing" && initialEditingStatusRef.current === BadgeStatus.Base.offer;
    // Rendering status dropdown in creation always, and in editing only for orders that started as Offer.
    const isStatusDropdownVisible = mode === "creation" || isEditableOfferFlow;

    // Loading customers only in creation because customer is immutable in editing.
    const { data: customerOptions, isLoading: isCustomerOptionsLoading } =
        useGetModuleCustomersQuery("sale", {
            skip: mode === "editing",
        });

    useEffect(() => {
        if (routerState?.customer) {
            setValue(
                "customer",
                {
                    id: routerState.customer.id,
                    name: routerState.customer.name,
                    code: routerState.customer.code,
                },
                { shouldValidate: true }
            );
        }
    }, [routerState]);

    return (
        <>
            <BaseInputsGrid>
                {/* Supplier */}
                <Controller
                    name="customer"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}
                            label={`${t("dropdowns.customer.label")} *`}
                            placeholder={t("dropdowns.customer.placeholder")}
                            options={customerOptions}
                            getter={{
                                label: (item) => item.name,
                                key: (item) => item.id,
                                caption: (item) => item.code ?? item.id,
                            }}
                            autocomplete
                            action={{
                                // title: t("dropdowns.customer.create"),
                                title: t("dropdowns.customer.buttons.create"),
                                onClick: () => setIsNewCustomerModalOpen(true),
                            }}
                            disableClearable
                            disabled={mode === "editing"}
                            isLoading={isCustomerOptionsLoading}
                            virtualize
                        />
                    )}
                />

                {/* Purchase date */}
                <Controller
                    name="order_date"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDatepicker
                            {...field}
                            {...fieldState}
                            label={`${t("fields.orderDate.label")} *`}
                            placeholder={t("fields.orderDate.placeholder")}
                            disabled={isInvoiced}
                        />
                    )}
                />

                {/* Preferred delivery date */}
                <Controller
                    name="preferred_delivery_date"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDatepicker
                            {...field}
                            {...fieldState}
                            label={t("fields.preferredDeliveryDate.label")}
                            placeholder={t("fields.preferredDeliveryDate.placeholder")}
                            disablePast
                            disabled={isInvoiced}
                        />
                    )}
                />

                {isStatusDropdownVisible && (
                    <Controller
                        name="status"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseDropdown
                                {...fieldState}
                                value={statusOptions.find((opt) => opt.value === field.value)}
                                label={t("dropdowns.status.label")}
                                placeholder={t("dropdowns.status.placeholder")}
                                options={statusOptions}
                                getter={{
                                    label: (item) => item.label,
                                    key: (item) => item.value,
                                    renderOption: (opt, icon) => (
                                        <div className="flex items-center gap-[8px]">
                                            {getOrderStatusIcon(opt.value)}
                                            <span className="grow">{opt.label}</span>
                                            {icon}
                                        </div>
                                    ),
                                }}
                                onChange={(newItem) => {
                                    if (!newItem) return;
                                    field.onChange(newItem.value);
                                }}
                                disableClearable
                            />
                        )}
                    />
                )}
            </BaseInputsGrid>

            <CreateCustomerModal
                isOpen={isNewCustomerModalOpen}
                onClose={() => setIsNewCustomerModalOpen(false)}
                onCreate={(cus) => {
                    setValue("customer", {
                        id: cus.id,
                        name: cus.name,
                        code: cus.code,
                    });

                    setIsNewCustomerModalOpen(false);
                }}
            />
        </>
    );
}
